import { useEffect, useState } from 'react';
import { IndianRupee, ShoppingCart, TrendingUp, Truck, XCircle, Users, Wallet, Percent } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Skeleton } from '../../components/skeleton';
import { StatCard } from '../../components/stat-card';
import { extractErr } from '../../api/error-message';

// Wired to GET /admin/analytics/reports — sales / operations / finance summary
// over a window. GMV, commission and payouts are measured on DELIVERED items.

const RANGES: { label: string; days: number }[] = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

interface Reports {
  windowDays: number;
  sales: { gmv: number; orders: number; avgOrderValue: number };
  operations: { deliveredItems: number; cancelledItems: number; activePartners: number };
  finance: { commissionEarned: number; sellerPayoutsReleased: number };
}

const inr = (n: number) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-3 text-[16px] font-semibold text-ink-900">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

export function SummaryTab() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState<Reports | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<Reports>('/admin/analytics/reports', { params: { days } })
      .then(({ data }) => { if (alive) setR(data); })
      .catch((err) => toast.error(extractErr(err)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13.5px] text-ink-500">Sales, operations &amp; finance · last {days} days</p>
        <div className="seg" role="group" aria-label="Report window">
          {RANGES.map((rg) => (
            <button key={rg.days} type="button" onClick={() => setDays(rg.days)} aria-pressed={days === rg.days}>
              {rg.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !r ? (
        <div role="status" aria-busy="true" aria-label="Loading" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="panel p-[18px]">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-4 h-8 w-36" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          <Section title="Sales">
            <StatCard icon={<IndianRupee size={18} />} label="GMV (delivered)" value={inr(r.sales.gmv)} />
            <StatCard icon={<ShoppingCart size={18} />} label="Orders" value={String(r.sales.orders)} />
            <StatCard icon={<TrendingUp size={18} />} label="Avg. order value" value={inr(r.sales.avgOrderValue)} />
          </Section>

          <Section title="Operations">
            <StatCard icon={<Truck size={18} />} label="Delivered items" value={String(r.operations.deliveredItems)} />
            <StatCard icon={<XCircle size={18} />} label="Cancelled items" value={String(r.operations.cancelledItems)} />
            <StatCard icon={<Users size={18} />} label="Active partners" value={String(r.operations.activePartners)} />
          </Section>

          <Section title="Finance">
            <StatCard icon={<Percent size={18} />} label="Commission earned" value={inr(r.finance.commissionEarned)} />
            <StatCard icon={<Wallet size={18} />} label="Seller payouts released" value={inr(r.finance.sellerPayoutsReleased)} />
          </Section>
        </div>
      )}
    </div>
  );
}
